import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    IconButton,
    Tooltip,
    useTheme,
} from '@mui/material';
import {
    MonitorHeart as OverviewIcon,
    Hub as ControlPlaneIcon,
    Business as OrgsIcon,
    Email as EmailIcon,
    Dns as DnsIcon,
    Logout as LogoutIcon,
    ArrowBack as BackIcon,
    AdminPanelSettings as OperatorIcon,
    Brightness4 as DarkModeIcon,
    Brightness7 as LightModeIcon,
    Security as SecurityIcon,
    Storage as ClickHouseIcon,
    Router as GatewayIcon,
    History as AuditIcon,
    Key as LicenseKeyIcon,
    Cloud as CloudAgentsIcon,
    Download as DownloadIcon,
    Category as SubscriptionPlansIcon,
    VpnKey as AiKeysIcon,
    IntegrationInstructions as RumSdkIcon,
    AccountTree as WorkbooksIcon,
    HowToReg as WaitlistIcon,
    AllInbox as ContactInboxIcon,
    MenuBook as DocsIcon,
    Payments as StripeIcon,
    Tune as FeatureAccessIcon,
    Terminal as ContainerLogsIcon,
    GppMaybe as CveFeedIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ColorModeContext } from '../../theme';

// Sidebar groups — Platform Controls run the actual product (orgs,
// plans, infra), Website Controls run the public marketing surface
// (waitlist for now; future: blog posts, changelog, status banner).
const menuGroups = [
    {
        label: 'Platform Controls',
        items: [
            { title: 'Platform Overview', path: '/operator/overview', icon: <OverviewIcon /> },
            { title: 'Control Plane', path: '/operator/control-plane', icon: <ControlPlaneIcon /> },
            { title: 'Organizations', path: '/operator/organizations', icon: <OrgsIcon /> },
            { title: 'Plans & Billing', path: '/operator/plans-billing', icon: <SubscriptionPlansIcon /> },
            { title: 'AI Keys', path: '/operator/ai-keys', icon: <AiKeysIcon /> },
            { title: 'RUM SDK Settings', path: '/operator/rum-sdk', icon: <RumSdkIcon /> },
            { title: 'Security', path: '/operator/security', icon: <SecurityIcon /> },
            { title: 'Email Settings', path: '/operator/email', icon: <EmailIcon /> },
            { title: 'System Connections', path: '/operator/system', icon: <DnsIcon /> },
            { title: 'Storage Clusters', path: '/operator/clickhouse-nodes', icon: <ClickHouseIcon /> },
            { title: 'Contact Inbox', path: '/operator/contact-inbox', icon: <ContactInboxIcon /> },
            { title: 'Workbooks', path: '/operator/workbooks', icon: <WorkbooksIcon /> },
            { title: 'Feature Access', path: '/operator/feature-access', icon: <FeatureAccessIcon /> },
            // The shared CVE advisory corpus. Platform-scope by definition:
            // one copy serves every tenant, so syncing and wiping it are
            // operator actions and have no tenant equivalent.
            { title: 'Vulnerability Feed', path: '/operator/cve-feed', icon: <CveFeedIcon /> },
            { title: 'Cloud Agents', path: '/operator/cloud-agents', icon: <CloudAgentsIcon /> },
            { title: 'Gateways', path: '/operator/gateways', icon: <GatewayIcon /> },
            { title: 'Container Logs', path: '/operator/container-logs', icon: <ContainerLogsIcon /> },
            { title: 'Downloads', path: '/operator/downloads', icon: <DownloadIcon /> },
            { title: 'License', path: '/operator/license', icon: <LicenseKeyIcon /> },
            { title: 'Audit Log', path: '/operator/audit', icon: <AuditIcon /> },
        ],
    },
    {
        label: 'Website Controls',
        items: [
            { title: 'Waitlist', path: '/operator/waitlist', icon: <WaitlistIcon /> },
            { title: 'Documentation', path: '/operator/documentation', icon: <DocsIcon /> },
        ],
    },
];

const OperatorSidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, isSuperAdmin, isWebAdmin } = useAuth();
    const theme = useTheme();
    const colorMode = useContext(ColorModeContext);
    const isDark = theme.palette.mode === 'dark';

    // Trim the sidebar to the role's surface. WEB_ADMIN sees only
    // Website Controls; SUPER_ADMIN sees both groups.
    const visibleGroups = isWebAdmin && !isSuperAdmin
        ? menuGroups.filter((g) => g.label === 'Website Controls')
        : menuGroups;
    const operatorTitle = isWebAdmin && !isSuperAdmin ? 'Website Operator' : 'Operator Panel';
    const operatorSubtitle = isWebAdmin && !isSuperAdmin
        ? 'Marketing surface administration'
        : 'Platform Administration';
    const operatorRoleLabel = isWebAdmin && !isSuperAdmin ? 'Website Operator' : 'Operator';

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <Box
            sx={{
                width: 240,
                height: '100vh',
                bgcolor: isDark ? '#0d1117' : '#ffffff',
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
            }}
        >
            {/* Logo / Header */}
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: isDark
                            ? 'rgba(63, 185, 80, 0.12)'
                            : 'rgba(26, 127, 55, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <OperatorIcon sx={{ fontSize: 20, color: 'success.main' }} />
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.2 }}>
                        {operatorTitle}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1 }}>
                        {operatorSubtitle}
                    </Typography>
                </Box>
            </Box>

            {/* Navigation */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {visibleGroups.map((group, groupIdx) => (
                    <React.Fragment key={group.label}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                px: 2,
                                pt: groupIdx === 0 ? 1.5 : 2,
                                pb: 0.5,
                                color: 'text.disabled',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                fontSize: '0.68rem',
                            }}
                        >
                            {group.label}
                        </Typography>
                        <List sx={{ px: 1, py: 0 }}>
                            {group.items.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                                        <ListItemButton
                                            onClick={() => navigate(item.path)}
                                            selected={isActive}
                                            sx={{
                                                borderRadius: 1,
                                                py: 1,
                                                '&.Mui-selected': {
                                                    bgcolor: isDark
                                                        ? 'rgba(110, 168, 254, 0.12)'
                                                        : 'rgba(9, 105, 218, 0.08)',
                                                    '&:hover': {
                                                        bgcolor: isDark
                                                            ? 'rgba(110, 168, 254, 0.16)'
                                                            : 'rgba(9, 105, 218, 0.12)',
                                                    },
                                                },
                                                '&:hover': {
                                                    bgcolor: isDark
                                                        ? 'rgba(255, 255, 255, 0.06)'
                                                        : 'rgba(0, 0, 0, 0.03)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 36,
                                                    color: isActive ? 'primary.main' : 'text.secondary',
                                                }}
                                            >
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.title}
                                                primaryTypographyProps={{
                                                    variant: 'body2',
                                                    fontWeight: isActive ? 600 : 400,
                                                    color: isActive ? 'text.primary' : 'text.secondary',
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </React.Fragment>
                ))}
            </Box>

            <Divider />

            {/* Footer */}
            <Box sx={{ p: 1.5 }}>
                {/* Theme Toggle */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 0.5,
                        mb: 0.5,
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {isDark ? 'Dark mode' : 'Light mode'}
                    </Typography>
                    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                        <IconButton
                            onClick={colorMode.toggleColorMode}
                            size="small"
                            sx={{ color: 'text.secondary', padding: 0.5 }}
                        >
                            {isDark ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Switch-to-platform is SUPER_ADMIN-only — WEB_ADMIN
                    has no platform-side surface. */}
                {isSuperAdmin && (
                    <Tooltip title="Switch to Platform">
                        <ListItemButton
                            onClick={() => navigate('/choose-context')}
                            sx={{
                                borderRadius: 1,
                                py: 0.75,
                                '&:hover': {
                                    bgcolor: isDark
                                        ? 'rgba(255, 255, 255, 0.06)'
                                        : 'rgba(0, 0, 0, 0.03)',
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                                <BackIcon sx={{ fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Switch to Platform"
                                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                        </ListItemButton>
                    </Tooltip>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1,
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {user?.username}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            {operatorRoleLabel}
                        </Typography>
                    </Box>
                    <Tooltip title="Logout">
                        <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
                            <LogoutIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};

export default OperatorSidebar;
